// noinspection TypeScriptFieldCanBeMadeReadonly
import { EventEmitter } from 'events'
import {
  Box,
  Pallet,
  ProgramSettings,
  BoxPosition,
  MachineSettings, GripperTypeEnum, MultipickConfigurationEnum, RobotTypeEnum
} from '../../generated/model/models'
import { findBox180DegRotates } from '../algo/palletedgeAdapt'
import { runMain } from '../autopack/RecPartAlgorithm'
import { BoxDef, getBoxDef } from './boxDef'
import { getPalletDef, PalletDef } from './palletDef'
import {
  getRobotMovementNumber,
  renumberBoxes,
  checkSinglePlaceOk,
  checkGripperSections,
  checkGripperSectionsWholeGroup,
  checkDependencyOrder,
  getBoxRangesXY,
  isRangesOverlap,
  isCollisionGroup,
  checkDependencyOrder4section,
  alpha4Func, alpha5Func, getAlignedGroups, pusherPrepPossible, getAlignedGroupsInline, checkDependencyOrderInline
} from './layerSort'
import { View } from './view'
import { projX, projY, vectorX, vectorY } from './utils'
import BoxOrientationOnConveyorEnum = ProgramSettings.BoxOrientationOnConveyorEnum
import { BoxGroupData, BoxGroupDataItem } from '@/generated/model/boxGroupData'
import { Borders } from '@/generated/model/gripper'
import {
  detectGripperPayloadOverlap,
  getBoxGripperSection, getGripperBorders, getGripperInsidePalletPercentage,
  getMaxNumberBoxes, hasRotationLimitations,
  isGripperInsidePalletSingleBox, isInLine3, isQuadPick,
  isRCInlineMultipick, isRI20, isRotationAllowed
} from '@/libs/pattern/gripperUtils'
import { calculateGroupBorders, getBorderPoints, getBorders } from '@/libs/pattern/borderUtils'

interface PatternEditorOptions {
  autoOrder?: boolean
  multiSelect?: boolean
  side?: string
}

export interface PatternEditorState {
  positions: BoxPosition[]
  groups: number[][]
  gripperSections: number[][]
  pusherGroups: number[]
  ignoreGripperBoxes: number[]
  ignoreRotationBoxes: number[]
}

export interface MachineData {
  gripperType: GripperTypeEnum
  gripperSections: number
  gripperWidth: number
  gripperLength: number
  robotType: RobotTypeEnum
  pusher: boolean
  boxOrientationOnConveyor: BoxOrientationOnConveyorEnum
  pusherMaxBoxWidth?: number
  doublePlaceDisabled?: boolean
  freeBoxGrouping?: boolean
  multipickConfiguration?: MultipickConfigurationEnum
}

export class PatternEditor extends EventEmitter {

  private side = 'L'
  private autoOrder = true
  private multiSelect = false
  private _multiPlaceOk = false
  private _swapOk = false
  private _groupWithPusher = false
  private positions: BoxPosition[] = []
  private groups: number[][] = []
  private selectedGroup: number[] = []
  private pickSections: number[] = []
  private gripperSections: number[][] = []
  private selectedGroupGripperSection: number[] = []
  private placeholderSelectedGroup: number[] = []
  private selectedGroupWithPusher: boolean = false
  private pusherGroups: number[] = []
  private selectedBox: number = 0
  private selectedPositions: BoxPosition[] = []

  private view: View
  private pallet: Pallet
  private box: Box
  private boxDef = new BoxDef()
  private palDef = new PalletDef()
  private boxOrientationOnConveyor: ProgramSettings.BoxOrientationOnConveyorEnum
  private manualReorderMode: boolean = false
  private manualReorderList: BoxPosition[] = []
  private ignoreGripperOutside: boolean = false
  private ignoreGripperBoxes: number[] = []
  private gripperIssuesList: BoxPosition[] = []
  private ignoreBoxRotation: boolean = false
  private ignoreRotationBoxes: number[] = []
  private rotationIssuesList: BoxPosition[] = []
  private machineData: MachineData

  private tempGripperIssuesList: BoxPosition[] = []
  private tempRotationIssuesList: BoxPosition[] = []
  private tempIgnoreGripperBoxes: number[] = []
  private tempIgnoreRotationBoxes: number[] = []
  private isCustomSolution: boolean = false

  protected constructor (box: Box, pallet: Pallet, programSettings: ProgramSettings, machineSettings: MachineSettings, view: View, options?: PatternEditorOptions) {
    super()
    this.pallet = pallet
    this.box = box
    this.view = view
    if (options) {
      this.autoOrder = options.autoOrder != null ? options.autoOrder : this.autoOrder
      this.multiSelect = options.multiSelect != null ? options.multiSelect : this.multiSelect
      this.side = options.side != null ? options.side : this.side
    }

    // graphic box properties
    this.boxDef = getBoxDef(box, programSettings)
    this.palDef = getPalletDef(pallet)

    this.isCustomSolution = 'CUSTOM_SOLUTION' === programSettings.robotType
    this.boxOrientationOnConveyor = programSettings.boxOrientationOnConveyor ? programSettings.boxOrientationOnConveyor
      : ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING

    const isFreeBoxGrouping = RobotTypeEnum.GENERIC === machineSettings.robotType &&
      machineSettings.customSettings !== undefined && machineSettings.customSettings.freeBoxGrouping
    this.machineData = {
      gripperType: machineSettings.gripperSettings!.gripperType!,
      gripperSections: machineSettings.gripperSettings!.gripperSections!,
      gripperLength: machineSettings.gripperSettings!.gripperLength!,
      gripperWidth: machineSettings.gripperSettings!.gripperWidth!,
      robotType: programSettings.robotType ? programSettings.robotType : RobotTypeEnum.GENERIC,
      pusher: machineSettings.pusher!,
      boxOrientationOnConveyor: programSettings.boxOrientationOnConveyor ? programSettings.boxOrientationOnConveyor
        : ProgramSettings.BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING,
      pusherMaxBoxWidth: machineSettings.pusherMaxBoxWidth,
      doublePlaceDisabled: machineSettings.disableDoublePlace!,
      freeBoxGrouping: isFreeBoxGrouping,
      multipickConfiguration: machineSettings.gripperSettings!.multipickConfiguration
    }

    // ---------------------------------------------------------------
    // view handlers
    // ---------------------------------------------------------------
    this.view.onnewbox = this.onnewbox
    this.view.onmove = this.onmove
    this.view.onmodified = this.onmodified
    this.view.onselected = this.onselected
    this.view.ondelete = this.ondelete
  }

  // ==============================================================
  //   PUBLIC METHODS
  // ==============================================================

  setSnapMode (active: boolean): void {
    this.view.setSnapMode(active)
  }

  setAutoOrder (active: boolean): void {
    this.autoOrder = active
    if (active) {
      this.reorder()
      this.deselectAll()
    }
  }

  setIgnoreGripperOutside (active: boolean) {
    console.log('[DEBUG] set ignoreGripperOutside', active, this.selectedBox)
    this.ignoreGripperOutside = active
    if (this.multiSelect) {
      // recalculate if grouping is possible when selecting/deselecting the checkbox
      // noinspection JSIgnoredPromiseFromCall
      this.isGroupPossible(this.selectedGroup, this.side === 'L')
    }
    if (this.multiSelect || !this.view.isSelected(this.selectedBox)) {
      return
    }
    if (active && this.canIssuesBeIgnored(this.selectedBox , 'gripper')) {
      this.emit('show_confirm_ignore_gripper', true)
    } else if (!active && this.ignoreGripperBoxes.includes(this.selectedBox)) {
      this.emit('show_confirm_ignore_gripper', false)
    }
  }

  setIgnoreBoxRotation (active: boolean) {
    console.log('[DEBUG] set setIgnoreBoxRotation', active, this.selectedBox)
    this.ignoreBoxRotation = active
    if (this.multiSelect || !this.view.isSelected(this.selectedBox)) {
      return
    }
    if (active && this.canIssuesBeIgnored(this.selectedBox, 'rotation')) {
      this.emit('show_confirm_ignore_rotation', true)
    } else if (!active && this.ignoreRotationBoxes.includes(this.selectedBox)) {
      this.emit('show_confirm_ignore_rotation', false)
    }
  }

  setMultiSelect (active: boolean): void {
    console.log('[DEBUG] setMultiSelect - groups', JSON.stringify(this.groups), active)
    this.view.lockMovement(this.side === 'R') // never let RIGHT side to move boxes
    this.multiSelect = active
    this.selectedPositions = []

    if (!active) {
      this.view.deselectCollection(this.selectedGroup)
      this.resetSelectedGroupProps()
      this.emit('selectedMulti', [])
      this.handleGroupCollision([], false)
      this.multiPlaceOk = false
      this.emit('groupSelection', false)
      this.emit('groupWithPusher', false)
      this.showAllGripperIssues()
      // console.log('[DEBUG] groups', JSON.stringify(this.groups))
      return
    }
    this.handleGroupCollision([], false)
    this.resetSelectedGroupProps()
    this.view.clearSelection()
    this.emit('selectedMulti', [])
    this.emit('groupSelection', false)
    this.emit('groupWithPusher', false)
  }

  // TODO: autofill doesn't work for LONG_SIDE_LEADING !
  autofill(): void {
    const w = (this.box.lengthMargin ? this.box.lengthMargin : 0) * 2 +
      (this.box.length ? this.box.length : 0) // TODO: hack

    const h = (this.box.widthMargin ? this.box.widthMargin : 0) * 2 +
      (this.box.width ? this.box.width : 0) // TODO: hack

    const autofillResult = runMain(this.palDef.width! + this.palDef.overhangL! + this.palDef.overhangR!,
      this.palDef.height! + this.palDef.overhangB! + this.palDef.overhangT!, w, h)

    const tmp = (autofillResult && autofillResult.boxes) ? autofillResult.boxes.map((box: any, i: number) => {
      const position: BoxPosition = { id: 0, x: 0, y: 0, rotation: 0 }
      position.x = Math.floor(box[0] + (box[2] - box[0]) / 2 + 0.5)
      position.y = Math.floor(box[1] + (box[3] - box[1]) / 2 + 0.5)

      // check only if the box has horizontal or vertical position, optimize labels to outside later
      position.rotation = box[2] - box[0] > box[3] - box[1] ? 0 : 90
      // TODO: hack
      if (this.boxDef.width < this.boxDef.height) {
        position.rotation = box[2] - box[0] > box[3] - box[1] ? 90 : 0
      }
      return position
    }) : []

    this.applyState({ positions: tmp, groups: [], gripperSections: [], pusherGroups: [], ignoreGripperBoxes: [], ignoreRotationBoxes: [] }, true, true)
    this.save()
    this.center()
  }

  applyState (state: PatternEditorState, optimize = false, hard = true): void {
    // console.log('[DEBUG] applyState:before', JSON.stringify(state.positions))
    console.debug('[DEBUG] applyState:before groups', JSON.stringify(state.groups))
    console.debug('[DEBUG] applyState:before gripperSections', JSON.stringify(state.gripperSections))
    console.debug('[DEBUG] applyState:before pusherGroups', JSON.stringify(state.pusherGroups))
    console.debug('[DEBUG] applyState:before ignoreGripperBoxes', JSON.stringify(state.ignoreGripperBoxes))
    this.view.clearSelection()
    this.view.deselectCollection([])
    this.emit('groupSelection', false)
    this.emit('groupWithPusher', false)
    if (hard) {
      this.view.clear()
    }
    this.groups.length = 0
    this.gripperSections.length = 0
    this.pusherGroups.length = 0
    this.ignoreGripperBoxes.length = 0
    this.ignoreRotationBoxes.length = 0
    this.positions = [ ...JSON.parse(JSON.stringify(state.positions)) ]

    if (optimize) {
      this.optimizeLabels()
    }
    this.multiPlaceOk = false
    this.swapOk = false
    this.selectedGroup.length = 0
    this.selectedBox = 0
    this.selectedPositions = []

    this.view.updatePositions(this.positions)
    if (state.groups.length > 0) {
      this.groups = [ ...state.groups ]
    }
    if (state.gripperSections) {
      this.gripperSections = [ ...state.gripperSections ]
    }
    if (state.pusherGroups) {
      this.pusherGroups = [ ...state.pusherGroups ]
    }
    if (state.positions.length > 0 && state.ignoreGripperBoxes) {
      this.ignoreGripperBoxes = [...state.ignoreGripperBoxes]
    }
    if (state.positions.length > 0 && state.ignoreRotationBoxes
      && hasRotationLimitations (this.machineData.robotType, this.machineData.gripperType)) {
      this.ignoreRotationBoxes = [...state.ignoreRotationBoxes]
    }
    this.reorder()
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.view.updateGroups(this.calculateGroupBorders())
    this.emit('groups', this.groups)
    this.emit('selectedMulti', [])
    console.debug('[DEBUG] applyState:after groups', JSON.stringify(state.groups))
    console.debug('[DEBUG] applyState:after gripperSections', JSON.stringify(state.gripperSections))
    console.debug('[DEBUG] applyState:after pusherGroups', JSON.stringify(state.pusherGroups))
    console.debug('[DEBUG] applyState:after ignoreGripperBoxes', JSON.stringify(state.ignoreGripperBoxes))
  }

  getState (): PatternEditorState {
    return {
      positions: this.positions.map(p => Object.assign({}, p)),
      groups: this.groups.map(g => [ ...g ]),
      gripperSections: this.gripperSections.map(s => [ ...s ]),
      pusherGroups: this.pusherGroups,
      ignoreGripperBoxes: this.ignoreGripperBoxes,
      ignoreRotationBoxes: this.ignoreRotationBoxes
    }
  }

  center (): void {
    // make paddings similar
    const { top, right, bottom, left } = this.getBorders(this.positions)
    const vertex = {
      x: Math.floor(((this.pallet.length! - right) - left) / 2),
      y: Math.floor(((this.pallet.width! - bottom) - top) / 2)
    }
    this.positions.forEach(p => {
      p.x = p.x! + vertex.x
      p.y = p.y! + vertex.y
    })
    this.showAllGripperIssues()
    this.view.updatePositions(this.positions)
    this.view.updateGroups(this.calculateGroupBorders())

    // clear group selection
    if (this.selectedGroup.length > 1) {
      this.view.clearSelection ()
      this.view.deselectCollection (this.selectedGroup)
      this.emit ('groupSelection', false)
      this.multiPlaceOk = false
      this.selectedGroup.length = 0
    }
    this.save()
  }

  // checks if all grouped boxes will be ok when centered, i.e. no gripper outside of pallet issues
  canCenter(): BoxPosition[] {
    const {top, right, bottom, left} = this.getBorders (this.positions)
    const vertex = {
      x: Math.floor (((this.pallet.length! - right) - left) / 2),
      y: Math.floor (((this.pallet.width! - bottom) - top) / 2)
    }
    const tempPositions = [...this.positions]
    tempPositions.forEach (p => {
      p.x = p.x! + vertex.x
      p.y = p.y! + vertex.y
    })

    const issues = this.getGroupsWithGripperIssues (tempPositions)
    console.log ('canCenter - issues: ', JSON.stringify (issues))
    return issues
  }

  clear (): void {
    this.view.clear()
    this.positions.length = 0
    this.groups.length = 0
    this.gripperSections.length = 0
    this.pusherGroups.length = 0
    this.ignoreGripperBoxes.length = 0
    this.ignoreRotationBoxes.length = 0
    this.multiPlaceOk = false
    this.swapOk = false
    this.selectedGroup.length = 0
    // this.emit('multiSelect', false)
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.save()
  }

  rotate (angle: number): void {
    // console.log('PE.rotate ', angle)
    angle = angle ? angle : 90
    const id = this.view.rotateActive(angle)
    const position = this.positions.find(p => p.id === id)
    if (position) {
      // rotate angle (90 degrees) clockwise (-90 degrees in the pallet coordinate system)
      const modulo = (this.isCustomSolution) ? 180 : 360
      const newRotation = (360 + (position.rotation! - angle)) % modulo
      // console.log('rotate - newRotation: ', newRotation, position.rotation)
      position.rotation = newRotation
      const conflicts = this.collisionsDetect(position)
      this.view.drawConflicts([...conflicts.map(c => c.id!), ...this.overlapDetect()])
      this.showAllGripperIssues()

      // after some changes
      this.removeGroup(id)
      this.reorder()
      this.view.updateGroups(this.calculateGroupBorders())
      // console.log('[DEBUG] after rotate: positions', JSON.stringify(this.positions))
      this.save()

      // check if multiplace
      const collection = this.selectedGroup.filter(q => q !== id!)
      // noinspection JSIgnoredPromiseFromCall
      this.isGroupPossible(collection, this.side === 'L')
      // console.log('[DEBUG] on:rotate, multiPlaceOk', id, this.multiPlaceOk)
    }
  }

  delete (): void {
    this.removeBox(this.view.deleteActive(this.selectedBox))
  }

  destroyGroup (): void {
    console.log('destroy group, boxId=', this.selectedBox)
    const selectedIds = this.selectedGroup.length > 0 ? this.selectedGroup : [this.selectedBox]
    this.groups
      .filter(group => group.some(id => selectedIds.includes(id)))
      .map(group => group[0])
      .forEach(id => this.removeGroup(id))
    this.reorder()
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.view.updateGroups(this.calculateGroupBorders())
    if (this.multiSelect && this.selectedGroup.length > 0) {
      this.refreshSelectionAvailability()
    } else {
      this.emit('groupSelection', false)
    }
    this.save()
  }

  makeGroup (pusher: boolean = false): void {
    console.log('makeGroup selectedGroup', JSON.stringify(this.selectedGroup))
    console.log('makeGroup pickSections', JSON.stringify(this.pickSections))
    console.log('makeGroup boxDef', JSON.stringify(this.boxDef))
    console.log('makeGroup gripperSections', JSON.stringify(this.gripperSections))
    console.log('makeGroup pusherGroups', JSON.stringify(this.pusherGroups))

    if (isRCInlineMultipick(this.machineData) && !this.multiPlaceOk) {
      return
    }

    if (!getMaxNumberBoxes(this.machineData).includes(this.selectedGroup.length)
      && !(isRCInlineMultipick(this.machineData) && this.multiPlaceOk)
      && !this.machineData.freeBoxGrouping) {
      // not possible to group selected number of boxes
      return
    }
    const newGroup = [ ...this.selectedGroup ]
    const groupIndex = this.groups.push(newGroup)
    // console.log('added group at index ', groupIndex, this.gripperSections.length)
    if (this.gripperSections.length === 0) {
      this.gripperSections = Array.from(new Array(groupIndex - 1), () => [])
    }
    this.gripperSections.push([ ...this.pickSections ])
    // console.log('[DEBUG] makeGroup - gripperSections:', JSON.stringify(this.gripperSections))

    if (this.machineData.pusher) {
      const value = pusher ? 1 : 0
      this.pusherGroups.push(value)
      // console.log('makeGroup pusherGroups - after', JSON.stringify(this.pusherGroups), index, groupIndex)
    }

    this.reorder()
    this.view.updateGroups(this.calculateGroupBorders())
    this.showAllGripperIssues()
    this.showAllConflicts()
    this.view.clearSelection()
    this.view.deselectCollection(this.selectedGroup)
    this.emit('groupSelection', false)
    this.emit('groupWithPusher', false)
    this.emit('groups', this.groups)
    this.multiPlaceOk = false
    this.pickSections.length = 0
    this.resetSelectedGroupProps()
    this.save()
    this.selectedBox = 0
  }

  // swapBoxes (): boolean {
  //   if (this.selectedGroup.length !== 2) {
  //     return false
  //   }
  //   const i1 = this.positions.findIndex(p => p.id === this.selectedGroup[0])
  //   const i2 = this.positions.findIndex(p => p.id === this.selectedGroup[1])
  //   if (i1 < 0 || i2 < 0) {
  //     return false
  //   }
  //   const tmp = this.positions[i1]
  //   this.positions[i1] = this.positions[i2]
  //   this.positions[i2] = tmp
  //   this.view.clearSelection()
  //   this.view.deselectCollection(this.selectedGroup)
  //   this.selectedGroup.length = 0
  //   this.view.updateOrder(this.positions, this.groups)
  //   this.save()
  //   this.swapOk = false
  //   return true
  // }

  reorderManually (active: boolean): void {
    if (!active) {
      if (this.manualReorderList.length === this.positions.length) {
        // manual_reorder is finished. update positions
        this.positions = [ ...this.manualReorderList ]
      }
      this.manualReorderList = []
      this.view.lockMovement(false)
      this.manualReorderMode = false
      // console.log('[DEBUG] patternEditor', 'manual_reorder_finished')
      this.emit('manual_reorder_finished')
      // console.log('[DEBUG] reordered manually, save')
      // console.log(JSON.stringify(this.positions, null, '  '))
      this.save()
      return
    }
    this.manualReorderMode = true
    this.view.updateOrder([], [])
    this.view.bringToFrontGroups()
    this.view.lockMovement(true)
  }

  collisionsDetect (p: BoxPosition): BoxPosition[] {
    const pRangesXY = getBoxRangesXY(p, this.boxDef)
    // noinspection UnnecessaryLocalVariableJS
    const collisions = this.positions.filter(pos => {
      const posRangesXY = getBoxRangesXY(pos, this.boxDef)
      return pos.id !== p.id && (
        isRangesOverlap(pRangesXY[0], posRangesXY[0])
        && isRangesOverlap(pRangesXY[1], posRangesXY[1])
      )
    })
    return collisions.length > 0 ? [ ...collisions, p ] : collisions
  }

  overlapDetect(): number[] {
    const overlapMap = detectGripperPayloadOverlap(this.groups, this.gripperSections, this.positions, this.pusherGroups, this.boxDef, this.machineData, this.side)
    this.emit('overlappingGroups', overlapMap)
    const overlapIds: number[] = []
    overlapMap.forEach((value) => {
      overlapIds.push(...value)
    })
    return overlapIds
  }

  showAllGripperIssues() {
    if (!isRI20 (this.machineData.robotType)) {
      return
    }
    this.tempGripperIssuesList = []
    this.tempRotationIssuesList = []
    this.tempIgnoreGripperBoxes = [...this.ignoreGripperBoxes]
    this.tempIgnoreRotationBoxes = [...this.ignoreRotationBoxes]

    this.showGripperIssuesForSelectedGroup()

    // check for remaining positions
    const positions = this.positions.filter(p => !this.selectedGroup.includes(p.id!))
    positions.forEach (position => {
      // gripper outside check
      const section = getBoxGripperSection (position, this.groups, this.pusherGroups, this.gripperSections, this.machineData)
      this.updateGripperAndRotationIssuesLists(position, section)
    })
    this.tempGripperIssuesList = this.tempGripperIssuesList.filter (g => !this.tempIgnoreGripperBoxes.includes (g.id!))
    this.tempRotationIssuesList = this.tempRotationIssuesList.filter(r => !this.tempIgnoreRotationBoxes.includes(r.id!))

    this.view.drawGripperIssues(this.tempGripperIssuesList.map (c => c.id!))
    this.view.drawWarnings(this.tempIgnoreGripperBoxes)
    this.view.drawRotationIssues(this.tempRotationIssuesList.map (c => c.id!))
    this.view.drawRotationWarnings(this.ignoreRotationBoxes)

    this.emit('gripperIssues', this.tempGripperIssuesList.length > 0)
    this.emit('rotationIssues', this.tempRotationIssuesList.length > 0)
    this.emit('ignoredIssues', this.tempIgnoreGripperBoxes.length > 0, 'gripper')
    this.emit('ignoredIssues', this.tempIgnoreRotationBoxes.length > 0, 'rotation')

    if ((this.selectedGroup.length < 1 && this.placeholderSelectedGroup.length < 1)
      || (this.selectedGroup.length > 0 && this.isSelectedGroupGrouped())) {
      this.gripperIssuesList = [...this.tempGripperIssuesList]
      this.ignoreGripperBoxes = [...this.tempIgnoreGripperBoxes]
      this.rotationIssuesList = [...this.tempRotationIssuesList]
      this.ignoreRotationBoxes = [...this.tempIgnoreRotationBoxes]
    }
  }

  updateGripperAndRotationIssuesLists(position: BoxPosition, section: number) {
    if (isGripperInsidePalletSingleBox (position, section, this.boxDef, this.palDef, this.machineData, this.side)) {
      // remove from ignore list, if previously ignored gripper issues
      this.tempIgnoreGripperBoxes = this.tempIgnoreGripperBoxes.filter (g => position.id !== g)
    } else {
      this.tempGripperIssuesList.push(position)
    }
    // if position has issue, but belongs to selected group and was grouped, make it a warning
    if (this.selectedGroup.includes(position.id!) && this.tempGripperIssuesList.includes(position) && this.isSelectedGroupGrouped()) {
      if (this.tempIgnoreGripperBoxes.indexOf(position.id!) < 0) {
        this.tempIgnoreGripperBoxes.push(position.id!)
      }
      this.tempGripperIssuesList = this.tempGripperIssuesList.filter(g => position.id !== g)
    }
    // gripper allowed rotation
    if (isRotationAllowed (position, this.boxDef, this.palDef, this.side, this.machineData)) {
      this.tempIgnoreRotationBoxes = this.tempIgnoreRotationBoxes.filter (r => position.id !== r)
    } else {
      this.tempRotationIssuesList.push (position)
    }
  }

  showGripperIssuesForSelectedGroup() {
    if (!getMaxNumberBoxes(this.machineData).includes(this.selectedGroup.length)) {
      return
    }

    const positions = this.getSelectedGroupPositions()
    const group = this.getSelectedGroupOrder()
    const pusherGroup = this.getSelectedGroupPusherGroup()
    const gripperSections = this.getSelectedGroupGripperSections()
    // console.log('patternEditor.showGripperIssuesForSelectedGroup - group: ', JSON.stringify(group))
    // console.log('patternEditor.showGripperIssuesForSelectedGroup - gripperSections: ', JSON.stringify(gripperSections))
    positions.forEach (position => {
      if (!this.selectedGroup.includes(position.id!) && !this.placeholderSelectedGroup.includes(position.id!)) {
        return
      }
      // gripper outside check
      const section = getBoxGripperSection (position, [group], [pusherGroup], [gripperSections], this.machineData)
      this.updateGripperAndRotationIssuesLists(position, section)
    })
    this.tempGripperIssuesList = this.tempGripperIssuesList.filter(g => !this.tempIgnoreGripperBoxes.includes (g.id!))
    this.tempRotationIssuesList = this.tempRotationIssuesList.filter(r => !this.tempIgnoreRotationBoxes.includes(r.id!))
    // console.log('patternEditor.showGripperIssuesForSelectedGroup: ', JSON.stringify(this.tempIgnoreGripperBoxes), JSON.stringify(this.tempGripperIssuesList))
  }

  getSelectedGroupOrder(): number[] {
    if (this.selectedGroup.length !== 0 && this.isSelectedGroupGrouped()) {
      return this.selectedGroup
    }

    // if multiPlaceCheck succeeded, return selectedGroup as is
    if (this.placeholderSelectedGroup.length === 0) {
      return this.selectedGroup
    }

    // if multiplaceCheck failed, return the group order when check ignore gripper outside pallet
    return this.placeholderSelectedGroup
  }

  isSelectedGroupGrouped(): boolean {
    if (this.selectedGroup.length === 0) {
      return false
    }
    return this.groups.some(group => group.length === this.selectedGroup.length &&
        group.every((value, idx) => value === this.selectedGroup[idx]))
  }

  getSelectedGroupPusherGroup(): number {
    if (!this.machineData.pusher) {
      return 0
    }
    return this.selectedGroupWithPusher ? 1 : 0
  }

  getSelectedGroupPositions(): BoxPosition[] {
    let positions: BoxPosition[] = []
    const selectedBoxes = []
    for (const selected of this.selectedGroup) {
      selectedBoxes.push(this.positions.find(p => p.id === selected))
    }
    if (selectedBoxes.includes(undefined)) {
      return positions
    }
    positions = this.positions.filter(p => this.selectedGroup.includes(p.id!))
    return positions
  }

  getSelectedGroupGripperSections(): number[] {
    return this.selectedGroupGripperSection
  }

  resetSelectedGroupProps() {
    this.selectedGroup.length = 0
    this.placeholderSelectedGroup.length = 0
    this.selectedGroupGripperSection.length = 0
    this.selectedGroupWithPusher = false
  }

  handleGripperWarning () {
    if (this.selectedBox < 1) {
      return
    }
    const boxId = this.selectedBox
    // check if already on ignore list
    const index = this.ignoreGripperBoxes.findIndex(p => boxId === p)
    if (index > -1 && this.ignoreGripperBoxes.length > 0) {
      this.ignoreGripperBoxes.splice(index, 1)
      // console.log('handleGripperWarning, removed from ignore list - boxId=', boxId)
    } else {
      const bp = this.positions.find(p => boxId === p.id)
      if (bp != null && bp.id != null && index < 0) {
        this.ignoreGripperBoxes.push(bp.id)
        // console.log('handleGripperWarning, added to ignore list - boxId=', boxId)
      }
    }
    this.showAllGripperIssues()
    this.view.clearSelection()
    this.selectedBox = 0
    this.save()
  }

  handleGripperWarningAll() {
    if (this.gripperIssuesList.length === 0 && this.ignoreGripperBoxes.length > 0) {
      this.ignoreGripperBoxes.length = 0
    } else {
      this.ignoreGripperBoxes = [...this.ignoreGripperBoxes, ...this.gripperIssuesList.map(b => b.id!)]
      this.gripperIssuesList.length = 0
    }

    this.showAllGripperIssues ()
    this.view.clearSelection ()
    this.selectedBox = 0
    this.save()
  }

  handleRotationWarning() {
    if (this.selectedBox < 1) {
      return
    }
    const boxId = this.selectedBox
    // check if already on ignore list
    const index = this.ignoreRotationBoxes.findIndex(p => boxId === p)
    if (index > -1 && this.ignoreRotationBoxes.length > 0) {
      this.ignoreRotationBoxes.splice(index, 1)
      // console.log('handleRotationWarning, removed from ignore list - boxId=', boxId)
    } else {
      const bp = this.positions.find(p => boxId === p.id)
      if (bp != null && bp.id != null && index < 0) {
        this.ignoreRotationBoxes.push(bp.id)
        // console.log('handleRotationWarning, added to ignore list - boxId=', boxId)
      }
    }
    this.showAllGripperIssues()
    this.view.clearSelection()
    this.selectedBox = 0
    this.save()
  }

  handleRotationWarningAll() {
    if (this.rotationIssuesList.length === 0 && this.ignoreRotationBoxes.length > 0) {
      this.ignoreRotationBoxes.length = 0
    } else {
      this.ignoreRotationBoxes = [...this.ignoreRotationBoxes, ...this.rotationIssuesList.map (b => b.id!)]
      this.rotationIssuesList.length = 0
    }

    this.showAllGripperIssues ()
    this.view.clearSelection ()
    this.selectedBox = 0
    this.save ()
  }

  resetGroups(features: string[]) {
    console.log('[DEBUG] resetGroups - features: ', JSON.stringify(features))
    if (features.includes('pusher') && !features.includes('sections')) {
      for (const [key, pg] of this.pusherGroups.entries()) {
        if (pg !== 0) {
          const groupToRemove = this.groups[key]
          this.removeGroup(groupToRemove[0])
        }
      }
      this.pusherGroups.length = 0
    } else if (features.includes('doubleplace') && !features.includes('sections')) {
      // only remove double place groups
      for (let i = 0; i < this.groups.length; i++) {
        const isDoublePlace = this.gripperSections[i].some(g => g !== 0)
        if (isDoublePlace) {
          this.removeGroup(this.groups[i][0])
        }
      }
    } else if (features.includes('sections')) {
      const toRemove = this.groups.map(g => g[0])
      for (const remove of toRemove) {
        this.removeGroup(remove)
      }
    }
    this.reorder()
    this.showAllGripperIssues()
    this.showAllConflicts()
    this.emit('groupSelection', false)
    this.emit('groupWithPusher', false)
    this.save()
  }

  resetGroupsByPos(pos: BoxPosition[]) {
    console.log ('[DEBUG] resetGroups - positions: ', JSON.stringify (pos))
    for (const p of pos) {
      this.removeGroup(p.id!)
    }
    this.reorder ()
    this.showAllGripperIssues ()
    this.showAllConflicts()
    this.emit ('groupSelection', false)
    this.save ()
  }

  autoResolveGripperIssues() {
    console.log('patternEditor.autoRotate... all: ', JSON.stringify(this.gripperIssuesList))
    if (this.gripperIssuesList.length < 1) {
      return
    }
    // console.log('patternEditor.autoRotate... all: ', JSON.stringify(this.gripperIssuesList))
    for (const pos of this.gripperIssuesList) {
      // const canRotate = this.issueResolvedByRotation(pos)
      // // console.log('patternEditor.autoRotate... canRotate: ', pos.id, canRotate, this.side)
      // if (canRotate) {
      // rotate angle (180 degrees) clockwise (-180 degrees in the pallet coordinate system)
      pos.rotation = (360 + (pos.rotation! - 180)) % 360
      // visual rotation
      this.view.rotate180(pos.id!)
      // }
    }
    this.showAllGripperIssues()
    this.showAllConflicts()
    this.reorder()
    this.save()
  }

  canAllAutoRotate() {
    // console.log('canAllAutoRotate before: ', JSON.stringify(this.gripperIssuesList), this.side)
    for (const pos of this.gripperIssuesList) {
      if (!this.issueResolvedByRotation(pos)) {
        return false
      }
    }
    return true
  }

  /**
   * Check if it is possible to have two boxes in a group,
   * verify gripper is always inside the pallet
   * try to rotate one box if gripper is outside
   */
  async checkMultiPlaceOK(boxes: BoxPosition[], canRotate: boolean, settings: MachineData)
    : Promise<{ok: boolean, placeGroup?: BoxPosition[], pickGroup?: number[], withPusher?: boolean}> {
    const isSSL = this.machineData.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING'
    if (isQuadPick (settings)) {
      if (boxes.length === 4) {
        const alignedGroups = getAlignedGroups (boxes, this.box.length!, this.box.width!, isSSL)
        // console.log('checkMultiPlace - getAlignedGroups: ', JSON.stringify(alignedGroups))
        const numberOfVerticalAlignedBoxes = (alignedGroups.vertical.map(p => p.length)).reduce((accumulator, current) => accumulator + current)
        const numberOfHorizontalAlignedBoxes = (alignedGroups.horizontal.map(p => p.length)).reduce((accumulator, current) => accumulator + current)
        if (numberOfHorizontalAlignedBoxes + numberOfVerticalAlignedBoxes < 4) {
          return {ok: false}
        }

        const depCheck1 = checkDependencyOrder4section (boxes, this.boxDef, this.side, this.box, isSSL)
        console.log ('[DEBUG] check dependency inside the group', depCheck1.ok, JSON.stringify (depCheck1.ordered))
        const combinations11 = getDependedPlaceGroupCombinations (depCheck1.ordered)

        const sorted = [...boxes].sort (alpha5Func)
        const combinations22 = getMultiPlaceGroupCombinations (sorted, this.machineData)
        // If depCheck false, boxes will be placed independently, not blocking each other
        // Box coordinates can not give us gripper-section, we need to try different combinations.
        const combination = depCheck1.ok ? combinations11 : combinations22
        // check if 2 boxes are pusherVerticalAligned for different section order
        const boxesVerticalAligned = depCheck1.ok ? depCheck1.withPusher! :
          (equal (sorted[0].x!, sorted[1].x!) && sorted[0].rotation! % 180 === 0
            || equal (sorted[0].y!, sorted[1].y!) && sorted[0].rotation! % 180 === 90)
        console.log ('[DEBUG] boxesVerticalAligned: ', boxesVerticalAligned, JSON.stringify (sorted), depCheck1.withPusher)
        const sectionsOrderList4section = ((depCheck1.ok && depCheck1.sectionOrderList != null) ? depCheck1.sectionOrderList : (boxesVerticalAligned ? [[1, 3, 4, 2], [4, 2, 1, 3]] : [[1, 2, 3, 4], [4, 3, 2, 1]]))
        console.log ('[DEBUG] sectionsOrderList4section: ', JSON.stringify (sectionsOrderList4section))

        const checkWithoutIgnoreGripperOutside = this.check4SectionGroupCombinations(combination, sectionsOrderList4section, false)
        if (checkWithoutIgnoreGripperOutside.ok) {
          this.selectedGroupGripperSection = checkWithoutIgnoreGripperOutside.pickGroup!
          this.selectedGroupWithPusher = checkWithoutIgnoreGripperOutside.withPusher!
          return checkWithoutIgnoreGripperOutside
        } else if (this.ignoreGripperOutside) {
          const checkWithIgnoreGripperOutside = this.check4SectionGroupCombinations(combination, sectionsOrderList4section, true)
          if (checkWithIgnoreGripperOutside.ok) {
            this.selectedGroupGripperSection = checkWithIgnoreGripperOutside.pickGroup!
            this.selectedGroupWithPusher = checkWithIgnoreGripperOutside.withPusher!
            return checkWithIgnoreGripperOutside
          }
        }

        return {ok: false}
      } else {
        const result = checkMultiPlaceQP2Boxes(boxes, this.positions, this.boxDef, this.palDef, this.side, this.ignoreGripperOutside, settings)
        if (result.ok) {
          this.selectedGroupGripperSection = result.pickGroup!
          this.selectedGroupWithPusher = result.withPusher!
        }
        return result
      }
    }

    if (isInLine3(settings.robotType, settings.gripperSections)) {
      return this.checkMultiPlaceInline(boxes, this.positions, this.box, this.boxDef, this.palDef, this.side, this.ignoreGripperOutside, settings)
    }

    if (isRI20(settings.robotType) && settings.gripperSections === 2) {
      return this.checkMultiPlaceInline(boxes, this.positions, this.box, this.boxDef, this.palDef, this.side, this.ignoreGripperOutside, settings)
    }

    if (settings.gripperSections === 1 || settings.gripperSections === 2) {
      // Check if there is dependency inside the group for box placing order,
      // and they can be placed one only in one sequence
      const depCheck = checkDependencyOrder (boxes[0], boxes[1], this.boxDef, this.side)
      console.log ('[DEBUG] check dependency inside the group', JSON.stringify (depCheck))

      const combinations1 = getDependedPlaceGroupCombinations (depCheck.ordered)
        .filter (c => (!Boolean (canRotate) && c.name.includes ('no-rotated')) || canRotate)

      const combinations2 = getMultiPlaceGroupCombinations (boxes, this.machineData)
        .filter (c => (!Boolean (canRotate) && c.name.includes ('no-rotated')) || canRotate)

      // If depCheck false, boxes will be placed independently, not blocking each other
      // Box coordinates can not give us gripper-section, we need to try different combinations.
      // Try to rotate one box or another
      const combinations = depCheck.ok ? combinations1 : combinations2

      /** Gripper section variations, group: [A, B] where a box at position A must be placed first,
       * could be at any gripper section of [1, 2]
       *
       * Example ---
       * A places first, B second (dependency in placing)
       * Gripper might have:
       * - section 1 - A, section 2 - B. Gripper first goes with section 1 to position A, than section 2 to position B.
       *    or
       * - section 1 - B, section 2 - A. Gripper first goes with section 2 to position A, than section 1 to position B.
       *
       */
      const sectionsOrderList = [[1, 2], [2, 1]]

      for (const c of combinations) {
        console.log ('[DEBUG] checkGripperSections', c.name, '----------------------------------------')
        // For multi-place we need to check gripper-position, if it outside pallet or there is a box collision
        // with other boxes, placed before.

        for (const sectionsOrder of sectionsOrderList) {
          const gripperCheck = checkGripperSections (c.placeGroup, [], this.positions, sectionsOrder, this.boxDef, this.palDef, this.ignoreGripperOutside, this.machineData, false, false, this.side) // TODO fix with 2 section + pusher
          console.log (`[DEBUG] checkGripperSections, ok=${ gripperCheck.ok }`, c.name,
            'sections:', JSON.stringify (sectionsOrder.map (s => c.placeGroup[s - 1].id)),
            'places:', JSON.stringify (c.placeGroup.map (p => p.id)))

          this.handleGroupCollision (gripperCheck.overlapIds, false)

          if (gripperCheck.ok) {
            if (c.rotated != null) {
              // noinspection ES6MissingAwait
              c.rotated.forEach (async (c_rotated) => {
                await this.doVisualRotation (c_rotated)
              })
            }
            const placeGroupOrigins = c.placeGroup.map (p => this.positions.find (b => b.id === p.id)!)
            // const pickGroup = sectionsOrder.map(s => c.placeGroup[s-1])
            const pickGroup = [...sectionsOrder]
            this.selectedGroupGripperSection = [...sectionsOrder]
            this.placeholderSelectedGroup = placeGroupOrigins.map (b => b.id!)
            return {ok: true, placeGroup: placeGroupOrigins, pickGroup: pickGroup}
          }
        }
      }
    }
    return {ok: false}
  }

  checkMultiPlaceInline (boxes: BoxPosition[], positions: BoxPosition[], box: Box, boxDef: BoxDef,
                         palDef: PalletDef, palletSide: string, ignoreGripperOutside: boolean, machineData: MachineData):
    {ok: boolean, placeGroup?: BoxPosition[], pickGroup?: number[], withPusher?: boolean} {
    console.log ('[DEBUG] checkMultiPlaceInline ', JSON.stringify(boxes.map(b => b.id)))
    // get groups and section order combinations
    let group1: BoxPosition[]
    let group2: BoxPosition[]
    if (boxes.length > 2) {
      // make sure at least 2 boxes are aligned with same rotation
      const alignedBoxes = getAlignedGroupsInline (boxes, box.length!, box.width!, machineData.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING')
      console.log ('[DEBUG] checkMultiPlaceInline - alignedBoxes - inline3 :', JSON.stringify (alignedBoxes))
      if (alignedBoxes.vertical.length !== 2 && alignedBoxes.horizontal.length !== 2) {
        this.selectedGroupGripperSection.length = 0
        this.placeholderSelectedGroup.length = 0
        return {ok: false}
      }
      const aligned = (alignedBoxes.vertical.length > 1) ? alignedBoxes.vertical : alignedBoxes.horizontal
      group1 = [...aligned].sort(alpha4Func)
      group2 = boxes.filter (p => !group1.includes (p))
    } else {
      // DP with 2 boxes
      const sorted = [...boxes].sort(alpha4Func)
      group1 = [sorted[0]]
      group2 = [sorted[1]]
    }
    const depCheck = checkDependencyOrderInline(group1, group2, boxDef, palletSide)
    // console.log ('[DEBUG] checkMultiPlaceInline - checkDependencyOrderInline:', JSON.stringify (depCheck))
    if (depCheck.ok) {
      group1 = depCheck.orderedGroups[0]
      group2 = depCheck.orderedGroups[1]
    }
    let sectionsOrder: number[][]
    if (boxes.length === 2) {
      sectionsOrder = (machineData.gripperSections === 2) ? [[1, 2], [2, 1]] : [[1, 2, 0], [2, 1, 0]]
    } else {
      // 3 Boxes:
      // singleBox placed first => [1,  2, 3], [3,  1, 2]
      // singleBox placed last => [1, 2,  3], [2, 3,  1]
      const singleBoxPlacedFirst = group1.length === 1
      // console.log('[DEBUG] checkMultiPlaceInline - singleBoxPlacedFirst: ', singleBoxPlacedFirst)
      sectionsOrder = singleBoxPlacedFirst ? [[1, 2, 3], [3, 1, 2]] : [[1, 2, 3], [2, 3, 1]]
    }

    // check gripper outside, payload overlap, collision
    const checkWithoutIgnoreGripperOutside = this.checkGroupCombinationsInline(group1, group2, sectionsOrder, false)
    if (checkWithoutIgnoreGripperOutside.ok) {
      this.selectedGroupGripperSection = checkWithoutIgnoreGripperOutside.pickGroup!
      this.placeholderSelectedGroup = checkWithoutIgnoreGripperOutside.placeGroup!.map(b => b.id!)
      return checkWithoutIgnoreGripperOutside
    } else if (ignoreGripperOutside) {
      const checkWithIgnoreGripperOutside = this.checkGroupCombinationsInline(group1, group2, sectionsOrder, true)
      if (checkWithIgnoreGripperOutside.ok) {
        this.selectedGroupGripperSection = checkWithIgnoreGripperOutside.pickGroup!
        this.placeholderSelectedGroup = checkWithIgnoreGripperOutside.placeGroup!.map(b => b.id!)
        return checkWithIgnoreGripperOutside
      }
    }
    return {ok: false}
  }

  // Angle = 0 - is top oriented!
  async isGroupPossible(selectedGroup: number[], canRotate: boolean) {
    console.log('isGroupPossible', { selectedGroup }, this.machineData.freeBoxGrouping)
    if (this.machineData.freeBoxGrouping) {
      this.multiPlaceOk = true
      return
    }

    this.handleGroupCollision([], false)

    // verify selected boxes
    const selectedBoxes = []
    for (const selected of selectedGroup) {
      selectedBoxes.push(this.positions.find(p => p.id === selected))
    }
    if (selectedBoxes.includes(undefined)) {
      console.log('[ERROR] isGroupPossible() some selected box is null', selectedBoxes)
      this.multiPlaceOk = false
      return
    }
    const group = selectedBoxes as BoxPosition[]
    console.log('[DEBUG] isGroupPossible() group', JSON.stringify(group))
    const rcInlineMultipick = isRCInlineMultipick(this.machineData)
    const rcInlineGroup = this.getRCInlineMultipickGroup(group)
    const squareBox = this.box.length === this.box.width
    const standardGroupSize = getMaxNumberBoxes(this.machineData).includes(group.length)

    if (rcInlineMultipick && group.length > Math.floor(this.machineData.gripperLength / this.getDistanceOnConveyor())) {
      console.log ('[ERROR] isGroupPossible() group exceeds RC inline multipick gripper length')
      this.multiPlaceOk = false
      this.selectedGroupGripperSection.length = 0
      return
    }

    if (rcInlineMultipick && squareBox && rcInlineGroup.length < 1) {
      console.log ('[ERROR] isGroupPossible() RC inline multipick requires inline boxes')
      this.multiPlaceOk = false
      this.selectedGroupGripperSection.length = 0
      return
    }

    if (!standardGroupSize && rcInlineGroup.length < 1) {
      console.log (`[ERROR] isGroupPossible() groups work only for ${ getMaxNumberBoxes (this.machineData) } boxes`)
      this.multiPlaceOk = false
      this.selectedGroupGripperSection.length = 0
      return
    }

    // check collisions with other boxes
    const collisionBoxes = this.getCollisionsBoxes(group).map(b => b.id!)
    const collisions = [...isCollisionGroup (group, this.positions, this.groups, this.boxDef, this.side, this.machineData), ...collisionBoxes]
    console.log('[DEBUG] isGroupPossible - collisions: ', JSON.stringify(collisions))
    if (collisions.length > 0) {
      console.log ('[ERROR] isGroupPossible() isCollision = true', JSON.stringify (group))
      this.handleGroupCollision(collisions, true)
      this.multiPlaceOk = false
      return
    }

    if (!standardGroupSize && rcInlineGroup.length > 0) {
      this.selectedGroup = rcInlineGroup.map(b => b.id!)
      this.pickSections.length = 0
      this.selectedGroupGripperSection.length = 0
      this.placeholderSelectedGroup.length = 0
      this.multiPlaceOk = true
      this.groupWithPusher = false
      return
    }

    // Single place: e.g. pick once two boxes -> place once two boxes -----
    let singlePlaceCheck = checkSinglePlaceOk (group, this.box, this.machineData)

    if (singlePlaceCheck.ok && isRI20(this.machineData.robotType) && this.machineData.gripperSections > 1) {
      let sections: number[] = []
      if (isQuadPick (this.machineData)) {
        // generate sections depending on gripper & pusher
        if (singlePlaceCheck.withPusher && group.length === 4) {
          sections = [1, 2, 3, 4]
        } else if (singlePlaceCheck.withPusher && group.length === 2) {
          sections = [1, 3]
        } else if (group.length === 2) {
          sections = [1, 2]
        }
      } else if (isInLine3 (this.machineData.robotType, this.machineData.gripperSections)) {
        sections = [1, 2, 3]
      } else {
        sections = [1, 2]
      }
      this.selectedGroupWithPusher = singlePlaceCheck.withPusher!
      this.selectedGroupGripperSection = sections
      this.placeholderSelectedGroup = singlePlaceCheck.group.map(p => p.id!)
      const gripperCheck = checkGripperSections (singlePlaceCheck.group, [], this.positions, sections, this.boxDef,
          this.palDef, this.ignoreGripperOutside, this.machineData, singlePlaceCheck.withPusher!, singlePlaceCheck.ok, this.side)
      console.log('[DEBUG] isGroupPossible - singlePlace gripperCheck: ', gripperCheck, group.length)
      if (!gripperCheck.ok) {
        singlePlaceCheck.ok = false
      }
    }

    canRotate = canRotate && isRI20(this.machineData.robotType)
    if (canRotate && this.machineData.gripperSections !== 4 && !this.machineData.pusher) { // only left side
      // try rotating one box and do single place, if they have distance as on conveyor and aligned X or Y
      console.log ('[DEBUG] after first checkSinglePlaceOk', canRotate, singlePlaceCheck.ok, singlePlaceCheck.aligned, Math.abs (group[0].rotation! - group[1].rotation!))
      if (!singlePlaceCheck.ok && singlePlaceCheck.aligned && Math.abs (group[0].rotation! - group[1].rotation!) === 180) {
        await this.doVisualRotation (group[0].id!)
        singlePlaceCheck = checkSinglePlaceOk (group, this.box, this.machineData)
      }
    }
    console.log ('isSinglePlaceOK', singlePlaceCheck.ok)
    if (singlePlaceCheck.ok) {
      if (collisions.length > 0) {
        console.log ('[ERROR] isGroupPossible() isCollision = true', JSON.stringify (group))
        this.handleGroupCollision(collisions, true)
        return
      }
      this.selectedGroup = singlePlaceCheck.group.map (b => b.id!)
      this.pickSections = isRI20 (this.machineData.robotType) ? Array (this.machineData.gripperSections).fill (0) : [0, 0]
      this.multiPlaceOk = true
      this.groupWithPusher = singlePlaceCheck.withPusher!
      return
    }

    if (isRCInlineMultipick(this.machineData)) {
      this.multiPlaceOk = false
      return
    }

    if (isRI20(this.machineData.robotType) && this.machineData.doublePlaceDisabled) {
      console.log ('[INFO] grouping not possible - double place disabled', JSON.stringify (group))
      this.multiPlaceOk = false
      return
    }

    // Multi-place: e.g. pick once two boxes -> place twice by single -----
    const multiPlaceCheck = await this.checkMultiPlaceOK (group, canRotate, this.machineData)
    console.log('isMultiPlaceOK', multiPlaceCheck.ok, JSON.stringify(multiPlaceCheck.placeGroup), JSON.stringify(multiPlaceCheck.pickGroup))
    if (multiPlaceCheck.ok) {
      if (collisions.length > 0) {
        console.log ('[ERROR] isGroupPossible() isCollision = true', JSON.stringify (group))
        this.handleGroupCollision(collisions, true)
        return
      }
      this.selectedGroup = multiPlaceCheck.placeGroup!.map (b => b.id!)
      this.pickSections = [...multiPlaceCheck.pickGroup!]
      this.multiPlaceOk = true
      this.groupWithPusher = multiPlaceCheck.withPusher!
      this.showAllGripperIssues()
      return
    }

    if (collisions.length > 0) {
      console.log ('[ERROR] isGroupPossible() isCollision = true', JSON.stringify (group))
      this.multiPlaceOk = false
      return
    }

    this.multiPlaceOk = false
    this.showAllGripperIssues()
  }

  moveSelected(direction: string, selectedIds?: number[]) {
    // do not move for right side
    if (this.side === 'R') {
      return
    }

    let allSelected: BoxPosition[]
    if (selectedIds && selectedIds.length > 0) {
      const selected = this.expandGroupedIds(selectedIds)
      allSelected = this.positions.filter(p => selected.includes(p.id!))
    } else if (this.selectedPositions.length > 0) {
      allSelected = this.selectedPositions
    } else {
      const singleSelection = this.positions.find(p => p.id === this.selectedBox)
      const group = this.groups.find(g => g.includes(this.selectedBox))
      allSelected = group ? this.positions.filter(p => group.includes(p.id!)) : [singleSelection!]
    }
    if (allSelected.length < 1) {
      return
    }

    const movesOutsidePallet = allSelected.some (p => {
      const w = this.boxDef.width * projX (p.rotation!) + this.boxDef.height * projY (p.rotation!)
      const h = this.boxDef.width * projY (p.rotation!) + this.boxDef.height * projX (p.rotation!)
      const diffX = vectorX(this.palDef.displayRotation)
      const diffY = vectorY(this.palDef.displayRotation)
      let newX: number
      let newY: number
      if ('left' === direction) {
        newX = (this.isCustomSolution) ? p.x! + diffX : p.x! - diffX
        newY = p.y! + diffY
      } else if ('right' === direction) {
        newX = (this.isCustomSolution) ? p.x! - diffX : p.x! + diffX
        newY = p.y! - diffY
      } else if ('up' === direction) {
        newX = (this.isCustomSolution) ? p.x! + diffY : p.x! - diffY
        newY = p.y! - diffX
      } else if ('down' === direction) {
        newX = (this.isCustomSolution) ? p.x! - diffY : p.x! + diffY
        newY = p.y! + diffX
      }
      return this.isXOutsidePalletLeft (newX!, w)
        || this.isXOutsidePalletRight (newX!, w)
        || this.isYOutsidePalletTop (newY!, h)
        || this.isYOutsidePalletBottom (newY!, h)
    })
    if (movesOutsidePallet) {
      return
    }

    for (const position of allSelected) {
      if (!position) {
        continue
      }

      switch (direction) {
        case 'up':
          position.y = position.y! - vectorX (this.palDef.displayRotation)
          position.x = (this.isCustomSolution) ? position.x! + vectorY (this.palDef.displayRotation) : position.x! - vectorY (this.palDef.displayRotation)
          break
        case 'left':
          position.x = (this.isCustomSolution) ? position.x! + vectorX (this.palDef.displayRotation) : position.x! - vectorX (this.palDef.displayRotation)
          position.y = position.y! + vectorY (this.palDef.displayRotation)
          break
        case 'right':
          position.x = (this.isCustomSolution) ? position.x! - vectorX (this.palDef.displayRotation) : position.x! + vectorX (this.palDef.displayRotation)
          position.y = position.y! - vectorY (this.palDef.displayRotation)
          break
        case 'down':
          position.y = position.y! + vectorX (this.palDef.displayRotation)
          position.x = (this.isCustomSolution) ? position.x! - vectorY (this.palDef.displayRotation) : position.x! + vectorY (this.palDef.displayRotation)
          break
      }
    }

    this.view.updatePositions(this.positions, false)
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.view.updateGroups(this.calculateGroupBorders())
    if (allSelected.length === 1) {
      // only show for single selection
      this.view.showRulers(this.selectedBox)
    }
    if (this.selectedGroup.length > 0) {
      this.refreshSelectionAvailability()
    }
    this.save()
  }

  async selectAll() {
    if (this.positions.length < 1) {
      return
    }
    const allIds = this.positions.map(p => p.id!).filter(Number)
    this.selectedPositions = [...this.positions]
    this.selectedGroup = [...allIds]
    await this.refreshSelectionAvailability()
    this.view.selectCollection(this.selectedGroup)
    this.emit('selectedMulti', [...this.selectedGroup])
  }

  deselectAll() {
    const selectedIds = this.selectedPositions.length > 0
      ? this.selectedPositions.map(p => p.id!).filter(Number)
      : [...this.selectedGroup]
    if (selectedIds.length < 1) {
      return
    }
    this.selectedPositions = []
    this.view.deselectCollection(selectedIds)
    this.resetSelectedGroupProps()
    this.multiPlaceOk = false
    this.swapOk = false
    this.groupWithPusher = false
    this.handleGroupCollision([], false)
    this.emit('groupSelection', false)
    this.emit('selectedMulti', [])
  }

  showValidationResult(lpuid: number) {
    if (lpuid > 0) {
      const box = this.positions.find(bp => lpuid === bp.lpbuId)
      if (box) {
        this.view.highlightBox (box.id!)
      }
    } else {
      this.view.removeHighlightedBox()
    }
  }

  // =================================================================================================================
  //
  //  PRIVATE METHODS
  //
  // =================================================================================================================

  private ondelete = (id: number) => {
    this.removeBox(id)
  }

  private removeBox (id: number): void {
    if (id < 1) {
      return
    }
    const index = this.positions.findIndex(p => p.id === id)
    if (index > -1) {
      this.positions.splice(index, 1)
    }

    let idxIgnore = this.ignoreGripperBoxes.indexOf(id)
    while (idxIgnore > -1) {
      this.ignoreGripperBoxes.splice(idxIgnore, 1)
      idxIgnore = this.ignoreGripperBoxes.indexOf(id)
    }
    let idxIgnoreR = this.ignoreRotationBoxes.indexOf(id)
    while (idxIgnoreR > -1) {
      this.ignoreRotationBoxes.splice(idxIgnoreR, 1)
      idxIgnoreR = this.ignoreRotationBoxes.indexOf(id)
    }

    // after some changes
    this.checkAndRemoveGroup(id)

    this.reorder()
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.view.updateGroups(this.calculateGroupBorders())
    // this.view.updateGroupLabels(this.groups)
    this.selectedBox = 0
    this.selectedGroup = this.selectedGroup.filter(selectedId => selectedId !== id)
    this.emit('selected', -1)
    this.save()
  }

  private onnewbox = (p: BoxPosition) => {
    this.positions.push(JSON.parse(JSON.stringify(p)))
    // console.log('[DEBUG] onnewbox', this.positions)
  }

  private onmove = (p: BoxPosition) => {
    // this.view.removeConflicts()

    const position = this.positions.find(q => q.id === p.id)
    if (!position) {
      return
    }
    const moveIds = this.getMoveIds(p.id!)
    if (moveIds.length === 1) {
      this.deselectAll()
      position.x = p.x
      position.y = p.y
      position.rotation = p.rotation
      const movingConflicts = this.collisionsDetect(p)
      this.view.drawConflicts([...movingConflicts.map(c => c.id!), ...this.overlapDetect()])
      this.showAllGripperIssues()
      return
    }

    const dx = p.x! - position.x!
    const dy = p.y! - position.y!
    this.positions
      .filter(q => moveIds.includes(q.id!))
      .forEach(q => {
        q.x = q.x! + dx
        q.y = q.y! + dy
      })
    position.rotation = p.rotation
    // console.log('onmove: ', JSON.stringify(this.positions))

    if (moveIds.length > 1) {
      this.view.updatePositions(this.positions.filter(q => moveIds.includes(q.id!)), false)
      this.view.updateGroups(this.calculateGroupBorders())
    }
    const conflicts = this.getCollisionsBoxes(this.positions.filter(q => moveIds.includes(q.id!)))
    this.view.drawConflicts([...conflicts.map(c => c.id!), ...this.overlapDetect()])
    this.showAllGripperIssues()
  }

  private removeGroup (id: number) {
    if (this.checkAndRemoveGroup(id)) {
      this.view.updateGroups(this.calculateGroupBorders())
      // this.view.updateGroupLabels(this.groups)
    }
  }

  private onmodified = (p: BoxPosition) => {
    const moveIds = this.getMoveIds(p.id!)
    const group = this.groups.find(g => g.includes(p.id!))
    const position = this.positions.find(q => q.id === p.id)
    const before = position ? { x: position.x, y: position.y } : null
    this.keepBoxInsidePallet(p)
    if (moveIds.length > 1 && before) {
      const dx = p.x! - before.x!
      const dy = p.y! - before.y!
      this.positions
        .filter(q => moveIds.includes(q.id!) && q.id !== p.id)
        .forEach(q => {
          q.x = q.x! + dx
          q.y = q.y! + dy
        })
      this.view.updatePositions(this.positions.filter(q => moveIds.includes(q.id!)), false)
      this.view.updateGroups(this.calculateGroupBorders())
    }
    // console.log('[DEBUG] onmodified - after keep inside', JSON.stringify(p))
    if (this.selectedPositions.length > 0) {
      this.deselectAll()
    } else if (this.selectedGroup.length > 0) {
      this.refreshSelectionAvailability()
    }

    if (!group) {
      this.removeGroup(p.id!)
    }
    this.reorder()
    this.showAllConflicts()
    this.showAllGripperIssues()
    this.save()
  }

  private onselected = async (id: number) => {
    console.log('[DEBUG] onselected !', id, this.selectedBox, JSON.stringify(this.selectedGroup))
    if (id < 0) {
      this.emit('selected', id, 0, 0)
      this.emit('groupSelection', this.multiSelect ? this.hasGroupedSelection() : false)
      return
    }
    this.selectedBox = id

    // if (process.env.NODE_ENV === 'development') {
    //   const position = this.positions.find (p => id === p.id)!
    //   const gripperSection = getBoxGripperSection(position, this.groups, this.pusherGroups, this.gripperSections, this.machineData)
    //   const gripperBorder = getGripperBorders (position, gripperSection, this.boxDef, this.machineData, false)
    //   const points = getBorderPoints (gripperBorder)
    //   this.view.showGripperBorder (points)
    // }

    if (this.manualReorderMode) {
      const selected = this.groups.find(g => g.includes(id)) || [id]
      selected.forEach(boxId => {
        const found = this.positions.find(q => q.id === boxId)
        if (found && this.manualReorderList.find(p => p.id === found.id) === undefined) {
          this.manualReorderList.push(found)
        }
      })
      this.view.updateOrder(this.manualReorderList, this.groups)
      this.view.bringToFrontGroups()
      // console.log('[DEBUG] patternEditor', 'selected', this.manualReorderList.length, this.positions.length)
      if (this.manualReorderList.length === this.positions.length) {
        this.reorderManually(false)
      }
      return
    }

    if (!this.multiSelect) {
      if (this.ignoreGripperOutside && this.canIssuesBeIgnored(id, 'gripper')) {
        this.emit('show_confirm_ignore_gripper', true)
        return
      }
      if (this.ignoreBoxRotation && this.canIssuesBeIgnored(id, 'rotation')) {
        this.emit('show_confirm_ignore_rotation', true)
        return
      }
      this.emit('selected', id)
    }

    if (!this.ignoreGripperOutside && this.ignoreGripperBoxes.includes(id)) {
      this.emit('checkIgnoreGripper', true)
      this.setIgnoreGripperOutside(true)
    }
    if (!this.ignoreBoxRotation && this.ignoreRotationBoxes.includes(id)) {
      this.emit('checkIgnoreRotation', true)
      this.setIgnoreBoxRotation(true)
    }

    if (!this.multiSelect) {
      this.emit('groupSelection', this.hasGroupedSelection([id]))
      return
    }

    if (!this.positions.map(p => p.id).includes(id)) {
      // console.log('[DEBUG] exit 1', JSON.stringify(this.selectedGroup))
      return
    }
    const selectedIds = this.expandGroupedIds([id])
    // check if deselect
    const allSelected = selectedIds.every(selectedId => this.selectedGroup.includes(selectedId))
    if (allSelected) {
      this.selectedGroup = this.selectedGroup.filter(selectedId => !selectedIds.includes(selectedId))
      this.view.deselectCollection(selectedIds)
      await this.refreshSelectionAvailability()
      this.emit('selectedMulti', [...this.selectedGroup])
      this.showAllGripperIssues()
      console.log('[DEBUG] exit 2', JSON.stringify(this.selectedGroup))
      return
    }
    // Max boxes in a group
    const maxNumBoxes = Math.max(0, ...getMaxNumberBoxes(this.machineData))
    const newSelectedGroup = [...new Set([...this.selectedGroup, ...selectedIds])]
    const hasGroupedSelection = this.hasGroupedSelection(newSelectedGroup)
    if (!hasGroupedSelection && maxNumBoxes > 0 && this.selectedGroup.length >= maxNumBoxes
      && !isRCInlineMultipick(this.machineData) && !this.machineData.freeBoxGrouping) {
      console.log('[DEBUG] exit 3', JSON.stringify(this.selectedGroup))
      return
    }

    this.selectedGroup = newSelectedGroup
    console.log('[DEBUG] onselected selectedGroup', JSON.stringify(this.selectedGroup))

    await this.refreshSelectionAvailability()
    console.log('[DEBUG] onselected(), after refreshSelectionAvailability, selectedGroup is:', JSON.stringify(this.selectedGroup))

    this.view.selectCollection(this.selectedGroup)
    this.emit('selectedMulti', [...this.selectedGroup])
    console.log('[DEBUG] onselected groups', JSON.stringify(this.groups))
  }

  get multiPlaceOk (): boolean {
    return this._multiPlaceOk
  }

  set multiPlaceOk (value: boolean) {
    this._multiPlaceOk = value
    if (!value) {
      this.pickSections = []
    }
    this.emit('multiPlace', value)
  }

  get groupWithPusher (): boolean {
    return this._groupWithPusher
  }

  set groupWithPusher (value: boolean) {
    this._groupWithPusher = value
    this.emit('groupWithPusher', value)
  }

  // ========================================================================================================

  private get swapOk (): boolean {
    return this._swapOk
  }

  private set swapOk (value: boolean) {
    this._swapOk = value
    this.emit('swapOk', value)
  }

  private calculateGroupBorders (): Array<Array<{ x: number, y: number, pusher?: boolean, doublePlace?: boolean }>> {
    return calculateGroupBorders(this.getState(), this.machineData, this.boxDef)
  }

  private expandGroupedIds(ids: number[]): number[] {
    const selected = [...ids]
    this.groups
      .filter(group => group.some(id => ids.includes(id)))
      .forEach(group => selected.push(...group))
    return [...new Set(selected)]
  }

  private getMoveIds(id: number): number[] {
    const selectedPositionIds = this.selectedPositions.map(p => p.id!).filter(Number)
    if (selectedPositionIds.includes(id)) {
      return this.expandGroupedIds(selectedPositionIds)
    }
    if (this.selectedGroup.includes(id)) {
      return this.expandGroupedIds(this.selectedGroup)
    }
    return this.expandGroupedIds([id])
  }

  private hasGroupedSelection(ids: number[] = this.selectedGroup): boolean {
    return this.groups.some(group => group.some(id => ids.includes(id)))
  }

  private async refreshSelectionAvailability(): Promise<void> {
    const hasGroupedSelection = this.hasGroupedSelection()
    this.emit('groupSelection', hasGroupedSelection)
    if (hasGroupedSelection || this.selectedGroup.length < 2) {
      this.handleGroupCollision([], false)
      this.multiPlaceOk = false
      this.swapOk = false
      this.groupWithPusher = false
      return
    }

    await this.isGroupPossible(this.selectedGroup, this.side === 'L')
    this.checkSwapOk()
  }

  private keepBoxInsidePallet (p: BoxPosition) {
    const w = this.boxDef.width * projX(p.rotation!) + this.boxDef.height * projY(p.rotation!)
    const h = this.boxDef.width * projY(p.rotation!) + this.boxDef.height * projX(p.rotation!)
    if (this.isXOutsidePalletLeft(p.x!, w)) {
      p.x = (this.isCustomSolution) ? -this.palDef.overhangR + Math.round(w / 2 + 0.5)
        : -this.palDef.overhangL + Math.round(w / 2 + 0.5)
    } else if (this.isXOutsidePalletRight(p.x!, w)) {
      p.x = (this.isCustomSolution) ? this.palDef.width + this.palDef.overhangL - Math.round(w / 2)
        : this.palDef.width + this.palDef.overhangR - Math.round(w / 2)
    }
    if (this.isYOutsidePalletTop(p.y!, h)) {
      p.y = Math.round(h / 2 + 0.5) - this.palDef.overhangT
    } else if (this.isYOutsidePalletBottom(p.y!, h)) {
      p.y = this.palDef.height + this.palDef.overhangB - Math.round(h / 2)
    }
    this.view.updatePositions([ p ], false)
    this.positions.filter(q => q.id === p.id).forEach(t => {
      t.x = p.x
      t.y = p.y
    })
  }

  private isXOutsidePalletLeft (x: number, boxWidth: number) {
    const overhang = (this.isCustomSolution) ? this.palDef.overhangR : this.palDef.overhangL
    return x - boxWidth / 2 < 0 - overhang
  }

  private isXOutsidePalletRight (x: number, boxWidth: number) {
    const overhang = (this.isCustomSolution) ? this.palDef.overhangL : this.palDef.overhangR
    return x + boxWidth / 2 > this.palDef.width + overhang
  }

  private isYOutsidePalletTop (y: number, boxHeight: number) {
    return y - boxHeight / 2 < 0 - this.palDef.overhangT
  }

  private isYOutsidePalletBottom (y: number, boxHeight: number) {
    return y + boxHeight / 2 > this.palDef.height + this.palDef.overhangB
  }

  private getBorders (positions: Array<(BoxPosition | undefined)>): Borders {
    return getBorders(positions, this.boxDef)
  }

  private checkAndRemoveGroup (memberId: number): boolean {
    const index = this.groups.findIndex(group => group.includes(memberId))
    if (index > -1) {
      this.groups.splice(index, 1)
      if (this.gripperSections.length > 0) {
        this.gripperSections.splice(index, 1)
      }
      if (this.pusherGroups.length > 0) {
        this.pusherGroups.splice(index, 1)
      }
      console.log('[DEBUG] checkAndRemoveGroup groups', JSON.stringify(this.groups))
      console.log('[DEBUG] checkAndRemoveGroup gripperSections', JSON.stringify(this.gripperSections))
      console.log('[DEBUG] checkAndRemoveGroup pusherGroups', JSON.stringify(this.pusherGroups))
      this.emit('groups', this.groups)
      return true
    }
    return false
  }

  private optimizeLabels (): void {
    const env = { w: this.boxDef.width, h: this.boxDef.height, labelPos: this.boxDef.labelPos }
    const mirrorX = this.machineData.robotType === RobotTypeEnum.CUSTOM_SOLUTION
    const boxes = this.positions.map(p => ({
      x: mirrorX ? this.palDef.width - p.x! : p.x!,
      y: p.y,
      rotation: p.rotation,
      id: p.id,
      multiPickGroup: -1
    }))
    const rotates: any[] = findBox180DegRotates(env, boxes)
    rotates.forEach((rotate180, i) => {
      if (rotate180) {
        this.positions[i].rotation = (this.positions[i].rotation! + 180) % 360
      }
    })
  }

  private getBoxGroupData (isLeft: boolean): BoxGroupData {
    const boxGroupData: BoxGroupData = {}
    // make pick-ordered groups
    const placeOrderedGroups: number[][] = [ ...this.groups ]
    const gripperSections: number[][] = [ ...this.gripperSections ]
    // console.log('[DEBUG] getBoxData, groups', JSON.stringify(placeOrderedGroups))
    // console.log('[DEBUG] getBoxData, gripperSections', JSON.stringify(gripperSections))
    // console.log('[DEBUG] getBoxData, positions', JSON.stringify(this.positions))

    for (const pos of this.positions) {
      let pick = 0
      let place = 0
      const [ robotMovement, groupIndex ] = getRobotMovementNumber(pos.id!, this.positions, this.groups)

      if (groupIndex > -1) {
        // get place order
        const placeGroup = placeOrderedGroups[groupIndex]
        place = placeGroup.findIndex(p => p === pos.id) + 1

        // get pick order
        const gripperSectionsItem = gripperSections[groupIndex]
        if (gripperSectionsItem) {
          if (gripperSectionsItem.length >= place) {
            pick = gripperSectionsItem[place - 1]
          }

          if (gripperSectionsItem.length === 4) {
            // console.log('[DEBUG] boxGroupData: ', pick, place, placeGroup.length)
            if (pick === 0 || (placeGroup.length > 2 && place === 2)) {
              place = 1
            }
            if (placeGroup.length > 2 && (place === 3 || place === 4)) {
              place = 2
            }
          }

          if (gripperSectionsItem.length === 3) {
            let firstPlace = false
            if (placeGroup.length > 2) {
              const placeGroupPos = this.positions.filter(p => placeGroup.includes(p.id!))
              const aligned = getAlignedGroupsInline(placeGroupPos, this.box.length!, this.box.width!, this.machineData.boxOrientationOnConveyor === 'SHORT_SIDE_LEADING')
              const alignedGroup = (aligned.vertical.length > 1) ? aligned.vertical : ((aligned.horizontal.length > 1) ? aligned.horizontal : [])
              let otherBoxPlace = -1
              if (alignedGroup.length > 1 && alignedGroup.includes(pos)) {
                const otherBox = alignedGroup.filter(b => b.id !== pos.id)
                otherBoxPlace = placeGroup.findIndex(p => p === otherBox[0].id) + 1
              }
              firstPlace = (place === 1 && (otherBoxPlace < 0 || otherBoxPlace > 1)) || (place === 2 && otherBoxPlace < 2)
            } else {
              firstPlace = place === 1
            }

            if (pick === 0 || firstPlace) {
              place = 1
            } else {
              place = 2
            }
          }
        }
      }
      const isGroupedWithPusher = this.pusherGroups[groupIndex] === 1
      const item: BoxGroupDataItem = { group: robotMovement, pick: pick, place: place, pusher: isGroupedWithPusher }
      boxGroupData[pos.id!] = item
    }
    // Object.entries(boxGroupData).filter(p => (p[1].pick + p[1].place) > 0).forEach(q => {
    //   console.log('[DEBUG] boxGroupData', JSON.stringify(q))
    // })

    return boxGroupData
  }

  // private getBoxGroupData (isLeft: boolean): BoxGroupData {
  //   const boxGroupData: BoxGroupData = {}
  //
  //   // make pick-ordered groups
  //   const pickOrderedGroups: number[][] = []
  //   const placeOrderedGroups: BoxPosition[][] = []
  //
  //   for (let i = 0; i < this.groups.length; i++) {
  //     const groupPositions = this.positions.filter(p => this.groups[i].includes(p.id!))
  //
  //     // sortedGroupByPick is the same as gripper section
  //     // const sortedGroupByPick = this.groups[i].map(id => groupPositions.find(p => p.id === id))
  //     pickOrderedGroups.push(this.groups[i])
  //
  //     const sortedGroupByPlace = getPlaceOrder(groupPositions, isLeft)
  //     placeOrderedGroups.push(sortedGroupByPlace)
  //   }
  //   console.log('pickOrderedGroups', JSON.stringify(pickOrderedGroups))
  //   // console.log('this.groups', JSON.stringify(this.groups))
  //
  //   for (let i = 0; i < this.positions.length; i++) {
  //     let pick = 0
  //     let place = 0
  //     const pos = this.positions[i]
  //     const [robotMovement, groupIndex] = getRobotMovementNumber(pos.id!, this.positions, this.groups)
  //
  //     if (groupIndex > -1) {
  //       // get pick order
  //       pick = pickOrderedGroups[groupIndex].findIndex(p => p === pos.id!) + 1
  //
  //       // get place order
  //       place = placeOrderedGroups[groupIndex].findIndex(p => p.id === pos.id) + 1
  //     }
  //     const item: BoxGroupDataItem = { group: robotMovement, pick: pick, place: place }
  //     boxGroupData[pos.id!] = item
  //   }
  //   // console.log('boxGroupData', JSON.stringify(boxGroupData))
  //   return boxGroupData
  // }

  private reorder (): void {
    console.debug('[DEBUG] editor.reorder, side:', this.side)
    console.debug('[DEBUG] editor.reorder, groups:', JSON.stringify(this.groups))
    console.debug('[DEBUG] editor.reorder, gripperSections:', JSON.stringify(this.gripperSections))

    const isLeft = this.side === 'L'
    let boxGroupData: {}
    if (!this.autoOrder) {
      boxGroupData = this.getBoxGroupData(isLeft)
      this.view.updateGroupLabels(boxGroupData)
      this.view.updateOrder(this.positions, this.groups)
      return
    }
    console.log('[DEBUG] before renumber:', JSON.stringify(this.positions.map(p => p.id)))
    const sorted = renumberBoxes(this.positions, this.box, isLeft, this.groups, this.boxOrientationOnConveyor)
    this.positions.length = 0
    this.positions.push(...sorted)
    console.log('[DEBUG] after renumber:', JSON.stringify(this.positions.map(p => p.id)))
    boxGroupData = this.getBoxGroupData(isLeft)
    this.view.updateGroupLabels(boxGroupData)
    this.view.updateOrder(this.positions, this.groups)
    this.view.updateGroups(this.calculateGroupBorders())
  }

  private getCollisionsBoxes(boxes: BoxPosition[]) {
    const collisions: BoxPosition[] = []
    boxes.forEach(position => {
      if (!collisions.includes(position)) {
        collisions.push(...this.collisionsDetect(position))
      }
    })
    return collisions
  }

  private getAllConflicts() {
    let groupCollisions: number[] = []
    if (this.selectedGroup.length > 0) {
      const selBoxes = this.positions.filter(p => this.selectedGroup.includes(p.id!))
      groupCollisions = isCollisionGroup(selBoxes, this.positions, this.groups, this.boxDef, this.side, this.machineData)
    }
    const collisions = this.getCollisionsBoxes(this.positions)
    const allConflicts = [...collisions.map(b => b.id!), ...this.overlapDetect(), ...groupCollisions]
    return [...new Set(allConflicts)]
  }

  private showAllConflicts () {
    const conflicts = this.getAllConflicts()
    console.debug('[DEBUG] showAllConflicts: ', JSON.stringify(conflicts))
    this.view.drawConflicts(conflicts)
  }

  private checkSwapOk(): boolean {
    const selectedGroup = this.selectedGroup
    if (selectedGroup.length !== 2) {
      this.swapOk = false
      return false
    }
    this.swapOk = true
    return true
  }

  private getRCInlineMultipickGroup(group: BoxPosition[]): BoxPosition[] {
    const squareBox = this.box.length === this.box.width
    if (!isRCInlineMultipick(this.machineData) || group.length < 2 || (!squareBox && group.length < 3)) {
      return []
    }

    const alignedGroup = this.boxDef.labelPos === ProgramSettings.LabelPositionEnum.IGNORE ? group.map(b => ({ ...b, rotation: b.rotation! % 180 })) : group
    const aligned = getAlignedGroupsInline(alignedGroup, this.box.length!, this.box.width!, this.machineData.boxOrientationOnConveyor === BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING)
    const horizontal = aligned.horizontal.length === group.length
    const vertical = aligned.vertical.length === group.length
    const squareHorizontal = horizontal && aligned.horizontal[0].rotation! % 180 === 0
    const squareVertical = vertical && aligned.vertical[0].rotation! % 180 !== 0
    const inlineGroup = squareBox
      ? (squareHorizontal ? aligned.horizontal : (squareVertical ? aligned.vertical : []))
      : (vertical ? aligned.vertical : (horizontal ? aligned.horizontal : []))
    if (inlineGroup.length < 1) {
      return []
    }

    const distance = this.getDistanceOnConveyor()
    const coordinates = inlineGroup.map(b => vertical ? b.y! : b.x!).sort((a, b) => a - b)
    const touching = coordinates.every((c, i) => i === 0 || Math.abs(c - coordinates[i - 1] - distance) <= 1)
    return touching ? [...inlineGroup].sort(alpha4Func) : []
  }

  private check4SectionGroupCombinations(combination: Array<{name: string, rotated: number[]|null, placeGroup: BoxPosition[]}>, sectionsOrderList4section: number[][], ignoreGripper: boolean)
      : {ok: boolean, placeGroup?: BoxPosition[], pickGroup?: number[], withPusher?: boolean} {
    let placeGroupOrigins: BoxPosition[] = []
    let okResult = {}
    let insidePercentage = 0
    // console.log('check4SectionGroupCombinations ', ignoreGripper)
    for (const c of combination) {
      // For multi-place we need to check gripper-position, if it outside pallet or there is a box collision
      // with other boxes, placed before.
      const group1 = c.placeGroup.slice (0, 2)
      const group2 = c.placeGroup.slice (2)

      for (const sectionsOrder of sectionsOrderList4section) {
        console.log ('[DEBUG] checkGripperSections', c.name, JSON.stringify(sectionsOrder), JSON.stringify(c.placeGroup.map(b => b.id!)), '----------------------------------------')
        const sectionGroup1 = sectionsOrder.slice (0, 2)
        const sectionGroup2 = sectionsOrder.slice (2)
        const gripperCheckGroups = checkGripperSectionsWholeGroup (group1, group2, this.positions, sectionGroup1, sectionGroup2, this.boxDef, this.palDef, ignoreGripper, this.machineData, this.side)
        this.handleGroupCollision(gripperCheckGroups.overlapIds, false)

        if (gripperCheckGroups.outsideIds.length > 0 && this.selectedGroupGripperSection.length === 0) {
          this.selectedGroupGripperSection = [...sectionsOrder]
          placeGroupOrigins = c.placeGroup.map(p => this.positions.find(b => b.id === p.id)!)
          this.placeholderSelectedGroup = placeGroupOrigins.map (b => b.id!)
        }

        if (gripperCheckGroups.ok) {
          const group1Percentage = getGripperInsidePalletPercentage(group1, sectionGroup1, this.boxDef, this.palDef, this.machineData)
          const group2Percentage = getGripperInsidePalletPercentage(group2, sectionGroup2, this.boxDef, this.palDef, this.machineData)
          placeGroupOrigins = c.placeGroup.map (p => this.positions.find(b => b.id === p.id)!)
          const pickGroup = [...sectionsOrder]

          if (insidePercentage < 1) {
            insidePercentage = group1Percentage + group2Percentage
            okResult = {ok: true, placeGroup: placeGroupOrigins, pickGroup: pickGroup, withPusher: true}
          } else if (insidePercentage < (group1Percentage + group2Percentage)) {
            // return current selection directly
            return {ok: true, placeGroup: placeGroupOrigins, pickGroup: pickGroup, withPusher: true}
          }
        }
      }
    }

    if (Object.keys(okResult).length > 0) {
      this.handleGroupCollision([], false)
      // @ts-ignore
      return okResult
    }
    return {ok: false}
  }

  private checkGroupCombinationsInline(group1: BoxPosition[], group2: BoxPosition[], sectionsOrder: number[][], ignoreGripper: boolean)
    : {ok: boolean, placeGroup?: BoxPosition[], pickGroup?: number[], withPusher?: boolean} {
    const resultMap = new Map()
    for (const order of sectionsOrder) {
      const sectionGroup1 = order.slice(0, group1.length)
      const sectionGroup2 = order.slice(group1.length, group1.length + group2.length)
      // console.log('[DEBUG] checkMultiPlaceInline - sectionGroup1: ', JSON.stringify(sectionGroup1), JSON.stringify(group1))
      // console.log('[DEBUG] checkMultiPlaceInline - sectionGroup2: ', JSON.stringify(sectionGroup2), JSON.stringify(group2))
      const gripperCheck = checkGripperSectionsWholeGroup(group1, group2, this.positions, sectionGroup1, sectionGroup2, this.boxDef, this.palDef, ignoreGripper, this.machineData, this.side)

      this.handleGroupCollision(gripperCheck.overlapIds, false)
      console.log('[DEBUG] checkGroupCombinationsInline - gripperCheck: ', JSON.stringify(gripperCheck), JSON.stringify(order), ignoreGripper)
      if (gripperCheck.outsideIds.length > 0) {
        this.selectedGroupGripperSection = [...order]
        this.placeholderSelectedGroup = [...group1, ...group2].map(p => this.positions.find(b => b.id === p.id)!).map(b => b.id!)
      }

      if (gripperCheck.ok) {
        const group1Percentage = getGripperInsidePalletPercentage(group1, sectionGroup1, this.boxDef, this.palDef, this.machineData)
        const group2Percentage = getGripperInsidePalletPercentage(group2, sectionGroup2, this.boxDef, this.palDef, this.machineData)
        const placeGroupOrigins = [...group1, ...group2].map(p => this.positions.find(b => b.id === p.id)!)
        const pickGroup = [...order]
        resultMap.set(group1Percentage + group2Percentage, {ok: true, placeGroup: placeGroupOrigins, pickGroup: pickGroup, withPusher: false})
      }
    }

    if (resultMap.size > 0) {
      this.handleGroupCollision([], false)
      return getFirstMapValue(resultMap)
    }
    return {ok: false}
  }

  private handleGroupCollision(overlapIds: number[], isCollision: boolean) {
    const conflicts = [...new Set([...overlapIds, ...this.getAllConflicts()])] // include all current conflicts (unique ids)
    this.view.drawConflicts(conflicts)
    this.emit('group_placement_collision', overlapIds.length > 0 && isCollision)
    this.emit('group_placement_overlap', overlapIds.length > 0 && !isCollision)
  }

  private async doVisualRotation(id: number) {
    const box = this.positions.find (p => p.id === id)!
    console.log ('[INFO] box rotated to single/double place OK', box.id)
    await this.view.rotate180 (box.id!)
    box.rotation = (box.rotation! + 180) % 360
  }

  private getDistanceOnConveyor(): number {
    return this.boxOrientationOnConveyor === BoxOrientationOnConveyorEnum.SHORT_SIDE_LEADING
      ? this.box.length!
      : this.box.width!
  }

  private save(): void {
    const state: PatternEditorState = {
      positions: this.positions,
      groups: this.groups,
      gripperSections: this.gripperSections,
      pusherGroups: this.pusherGroups,
      ignoreGripperBoxes: this.ignoreGripperBoxes,
      ignoreRotationBoxes: this.ignoreRotationBoxes
    }

    this.emit('save', state)
  }

  private canIssuesBeIgnored (boxId: number, issueType: string): boolean {
    if (issueType === 'rotation') {
      return boxId !== 0 && this.rotationIssuesList.some(p => boxId === p.id) && !this.ignoreRotationBoxes.includes(boxId)
    } else {
      return boxId !== 0 && this.gripperIssuesList.some(p => boxId === p.id) && !this.ignoreGripperBoxes.includes(boxId)
    }
  }

  private issueResolvedByRotation(pos: BoxPosition): boolean {
    const testPos = {...pos}
    testPos.rotation = (360 + (testPos.rotation! - 180)) % 360
    return isGripperInsidePalletSingleBox (testPos, 1, this.boxDef, this.palDef, this.machineData, this.side)
  }

  private getGroupsWithGripperIssues(positions: BoxPosition[]) {
    const issues: BoxPosition[] = []
    positions.forEach (position => {
      if (this.groups.flat ().includes (position.id!)
        && !isGripperInsidePalletSingleBox (position, 1, this.boxDef, this.palDef, this.machineData, this.side)) {
        issues.push (position)
      }
    })
    // console.log ('center - getGroupsWithGripperIssues: ', JSON.stringify (issues))
    return issues
  }

}

const getRotated = (box: BoxPosition): BoxPosition => {
  return { ...box, rotation: (box.rotation! + 180) % 360 }
}

const getDependedPlaceGroupCombinations = (bb: BoxPosition[]): Array<{name: string, rotated: number[] | null, placeGroup: BoxPosition[]}> => {
  if (bb.length < 2) {
    return []
  }
  if (bb.length === 4) {
    return [
      {name: '1-no-rotated-depended-place', rotated: null, placeGroup: bb}
    ]
  }
  return [
    {name: 'no-rotated-depended-place', rotated: null, placeGroup: [bb[0], bb[1]]},
    {name: '1-rotated-depended-place', rotated: [bb[0].id!], placeGroup: [getRotated (bb[0]), bb[1]]},
    {name: '2-rotated-depended-place', rotated: [bb[1].id!], placeGroup: [bb[0], getRotated (bb[1])]},
  ]
}

const getMultiPlaceGroupCombinations = (bb: BoxPosition[], settings: MachineData): Array<{ name: string, rotated: number[] | null, placeGroup: BoxPosition[] }> => {
  if (bb.length === 4) {
    return [
      { name: 'no-rotated-normal-place', rotated: null, placeGroup: bb },
     ]
  }
  if (bb.length === 2 && settings.gripperSections === 4) {
    return [
      {name: 'no-rotated-normal-place', rotated: null, placeGroup: [bb[0], bb[1]]},
      {name: 'no-rotated-swapped-place', rotated: null, placeGroup: [bb[1], bb[0]]}
    ]
  }
  return [
    { name: 'no-rotated-normal-place', rotated: null, placeGroup: [bb[0], bb[1]] },
    { name: 'no-rotated-swapped-place', rotated: null, placeGroup: [bb[1], bb[0]] },
    { name: '1-rotated-normal-place', rotated: [bb[0].id!], placeGroup: [getRotated(bb[0]), bb[1]] },
    { name: '2-rotated-normal-place', rotated: [bb[1].id!], placeGroup: [bb[0], getRotated(bb[1])] },
    { name: '2-rotated-swapped-place', rotated: [bb[1].id!], placeGroup: [getRotated(bb[1]), bb[0]] },
    { name: '1-rotated-swapped-place', rotated: [bb[0].id!], placeGroup: [bb[1], getRotated(bb[0])] },
    {
      name: 'all-rotated-normal-place',
      rotated: [bb[0].id!, bb[1].id!],
      placeGroup: [getRotated(bb[0]), getRotated(bb[1])]
    },
    {
      name: 'all-rotated-swapped-place',
      rotated: [bb[1].id!, bb[0].id!],
      placeGroup: [getRotated(bb[1]), getRotated(bb[0])]
    },
  ]
}

const equal = (a: number, b: number) => {
  //
  // Maximum tolerance to aligned boxes
  // epsilon
  //
  const EPSILON = 0 // robot can not place a group with boxes if they are not perfectly aligned
  return Math.abs(a - b) <= EPSILON
}

const getFirstMapValue = (resultMap: Map<any, any>) => {
  function compareKeys (a: number, b: number): number {
    return a > b ? -1 : 1
  }
  const sortedEntries = [...resultMap.entries()].sort((a, b) => compareKeys(a[0], b[0]))
  // console.log('sortedEntries: ', JSON.stringify(sortedEntries))
  const sortedMap = new Map(sortedEntries)
  const [firstValue] = sortedMap.values()
  return firstValue
}

export const checkMultiPlaceQP2Boxes = (boxes: BoxPosition[], positions: BoxPosition[], boxDef: BoxDef, palDef: PalletDef, palletSide: string, ignoreGripperOutside: boolean, machineData: MachineData)
  : {ok: boolean, placeGroup?: BoxPosition[], pickGroup?: number[], withPusher?: boolean} => {
  const depCheck2 = checkDependencyOrder (boxes[0], boxes[1], boxDef, palletSide)
  const combination = (depCheck2.ok) ? [{name: '1-no-rotated-depended-place', rotated: null, placeGroup: depCheck2.ordered}]
    : getMultiPlaceGroupCombinations(boxes, machineData)

  // gripper sections [1,2] or [1,3] => place order [1,2][2,1][1,3][3,1]
  // sections [1,3] are only possible if boxes can be prepared with pusher
  const pusherPossible = pusherPrepPossible(boxDef, machineData)
  const soList = (pusherPossible) ? [
    {sectionsOrder: [[1, 2], [2, 1]], pusherAligned: false},
    {sectionsOrder: [[1, 3], [3, 1]], pusherAligned: true}
  ] : [{sectionsOrder: [[1, 2], [2, 1]], pusherAligned: false}]

  const resultMap = new Map()
  for (const c of combination) {
    for (const so of soList) {
      for (const order of so.sectionsOrder) {
        const gripperCheck = checkGripperSections (c.placeGroup, [], positions, order, boxDef, palDef, ignoreGripperOutside, machineData, so.pusherAligned, false, palletSide)
        console.log('[DEBUG] checkMultiPlaceQP2Boxes - gripperCheck: ', JSON.stringify(gripperCheck.ok), JSON.stringify(c.name), JSON.stringify(c.placeGroup.map(b => b.id)), JSON.stringify(order))
        if (gripperCheck.ok) {
          const group1Percentage = getGripperInsidePalletPercentage([c.placeGroup[0]], [order[0]], boxDef, palDef, machineData)
          const group2Percentage = getGripperInsidePalletPercentage([c.placeGroup[1]], [order[1]], boxDef, palDef, machineData)
          const placeGroupOrigins = c.placeGroup.map (p => positions.find (b => b.id === p.id)!)
          const pickGroup = [...order, 0, 0]
          resultMap.set(group1Percentage + group2Percentage, {ok: true, placeGroup: placeGroupOrigins, pickGroup: pickGroup, withPusher: so.pusherAligned})
        }
      }
    }
  }

  if (resultMap.size > 0) {
    return getFirstMapValue(resultMap)
  }
  return {ok: false}
}
